import type { Definition } from 'typescript-json-schema';
import type { DecoratorContext } from '../interfaces';

/**
 * Resolves the current enum values from a property, following $ref if needed.
 *
 * @param property - The schema property definition
 * @param context - Runtime context for schema access
 * @returns The enum values array, or undefined if no enum is found
 */
export function resolveEnumFromProperty(property: Definition, context: DecoratorContext): string[] | undefined {
    if (property?.enum && Array.isArray(property.enum)) {
        return property.enum as string[];
    }

    if (property?.$ref) {
        const refName = property.$ref.replace('#/definitions/', '');
        const schema = context.schema?.get();
        const refDef = schema?.definitions?.[refName] as Definition;
        if (refDef?.enum && Array.isArray(refDef.enum)) {
            return refDef.enum as string[];
        }
    }

    return undefined;
}

/**
 * Applies filtered enum values to a property, inlining $ref types if needed.
 *
 * @param property - The schema property definition to modify
 * @param filteredEnum - The filtered list of allowed enum values
 * @param context - Runtime context for schema access
 */
export function applyFilteredEnumToProperty(
    property: Definition,
    filteredEnum: string[],
    context: DecoratorContext
): void {
    if (property.$ref) {
        const refName = property.$ref.replace('#/definitions/', '');
        const schema = context.schema?.get();
        const refDef = schema?.definitions?.[refName] as Definition;

        if (refDef?.type) {
            property.type = refDef.type;
        }

        delete property.$ref;
        property.enum = filteredEnum;
    } else {
        property.enum = filteredEnum;
    }
}
